import { useMemo } from 'react';
import { styled } from '@mui/material/styles';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import dynamic from 'next/dynamic';

import { ListIcons } from 'components/Stage/utils';
import { SellingPoints } from 'components/Stage/SellingPoints/SellingPoints';
import { LoanSelectorSkeleton } from 'components/LoanSelector/LoanSelectorSkeleton';
import { Badge } from '../StageParts/Badge';
import { PartnerPanels } from '../StageParts/PartnerPanels';
import { expandTextMacros } from 'lib/text-macros/textMacroHelper';

// Below-the-fold dynamic imports for better LCP
const LegalTextGoogle = dynamic(
  () =>
    import('components/Stage/LegalTextGoogle').then((m) => ({
      default: m.LegalTextGoogle,
    })),
  { ssr: true },
);

const LegalTextSEAShort = dynamic(
  () =>
    import('components/Stage/LegalText').then((m) => ({
      default: m.LegalTextSEAShort,
    })),
  { ssr: true },
);

const AppDownloadSection = dynamic(
  () =>
    import('../StageParts/AppDownloadSection').then((m) => ({
      default: m.AppDownloadSection,
    })),
  { ssr: true },
);

import type {
  ISeaStageWithMultibadgeFields,
  IStageBadge,
} from 'types/generated/contentful';
import { useDecision } from '@optimizely/react-sdk';
import { getAppRedirectionExperimentStageConfig } from 'components/ABTestVariations/CAT3820AppRedirectionV4/helpers';
import { ABTestKeys, Variation } from 'lib/helpers/optimizely/flagConfigs';
import { getOsName } from 'lib/router/route-helper';
import { Overlay } from 'components/ABTestVariations/CAT3820AppRedirectionV4/Overlay';

const LoanSelector = dynamic(
  () => import('components/LoanSelector').then((mod) => mod.LoanSelector),
  {
    loading: () => <LoanSelectorSkeleton />,
    ssr: true,
  },
);

const HEADLINE_DEFAULT = 'Günstige Kredite - garantiert!';

const Headline = styled(Typography)(({ theme }) => ({
  fontWeight: 500,
  fontSize: 26,
  width: '100%',
  marginBottom: 0,
  [theme.breakpoints.up('md')]: {
    lineHeight: '64px',
    fontSize: 48,
    fontWeight: 700,
    width: '90%',
  },
}));

const HeadlineGridItem = styled(Grid)(({ theme }) => ({
  padding: '0',
  height: 'auto',
  position: 'relative',
  [theme.breakpoints.up('md')]: { paddingLeft: '1%' },
}));

const DesktopBadgeWrapper = styled('div')`
  position: relative;
  top: 0px;
  right: 2px;
  display: flex;
  flex-direction: column;
  -webkit-box-align: center;
  align-items: center;
  margin: -35px 0px 0px -60px;
`;

const HideBadgeSmallerMedium = styled('div')(({ theme }) => ({
  [theme.breakpoints.down('md')]: { width: 0, display: 'none' },
}));

const PartnerPanelsWrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  overflowX: 'auto',
  overflowY: 'hidden',
  gap: theme.spacing(2),
  height: '100px',
  width: '100%',
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),

  // hide scrollbar but keep functionality
  scrollbarWidth: 'none',
  '&::-webkit-scrollbar': {
    display: 'none',
  },

  [theme.breakpoints.down('md')]: {
    marginTop: theme.spacing(2),
  },
}));

const LegalTextWrapper = styled('div')(({ theme }) => ({
  paddingRight: 0,
  width: '100%',

  [theme.breakpoints.up('md')]: {
    paddingRight: theme.spacing(2),
  },
}));

const LpContainer = styled(Container)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
}));

export const getTopBadgeForMobile = (badges: IStageBadge[] | undefined) => {
  // return the last badge for mobile or none
  if (!badges || badges.length === 0) return undefined;
  if (badges.length === 1) return badges[0];
  return badges[1];
};

export const getBottomBadgeForMobile = (badges: IStageBadge[] | undefined) => {
  // return the first badge for mobile or none
  if (!badges || badges.length <= 1) return undefined;
  return badges[0];
};

export type SeaStageWithMultiBadgesProps = ISeaStageWithMultibadgeFields;

export const SeaStageWithMultiBadges = (
  props: SeaStageWithMultiBadgesProps,
) => {
  const {
    headline,
    bulletPoints,
    badges,
    partners,
    showLegalText,
    showCreditcardOption,
    additionalParams,
    ctaText,
  } = props;

  // there can be only 0, 1 or 2 badges
  // the distiction into primary badge and secondary badge is important for mobile
  // only, as if there is only a main badge, it stays in the top of the stage,
  // if there is a secondary badge, the secondary badge takes the top position and
  // the main badge is moved to the bottom of the stage.
  // if there are no badges, the stage is displayed without badges.
  const areBadgesIncluded = badges && badges.length > 0;
  const mobileTopBadge = useMemo(() => getTopBadgeForMobile(badges), [badges]);
  const mobileBottomBadge = useMemo(
    () => getBottomBadgeForMobile(badges),
    [badges],
  );

  const isTvBannerEnabled = false;
  const isAppDownloadSectionEnabled = false; // AB Test for APP is currently off.
  const listIcon = ListIcons.CHECK_MARKS;
  const headlineWithMacros = expandTextMacros(headline);

  // Mobile detection for AB test - uses SSR-safe approach
  // This won't block LCP because Stage component renders independently

  const overrideAttributes = {
    overrideAttributes: {
      deviceOS: getOsName(),
    },
  };

  const [decisionCAT4168AppRedirectionV5] = useDecision(
    ABTestKeys.CAT_4600_APP_REDIRECTION_V3,
    undefined,
    { ...overrideAttributes },
  );

  const appRedirectionVariation =
    decisionCAT4168AppRedirectionV5.variationKey || null;

  const appRedirectionExperimentConfig = getAppRedirectionExperimentStageConfig(
    appRedirectionVariation || '',
  );

  return (
    <Box
      sx={{
        background:
          'linear-gradient(0deg, #f7f7f7 0%, rgba(247, 247, 247, 0) 100%)',
        padding: { xs: '24px 0 16px', md: '64px 0 32px' },
      }}
    >
      <LpContainer>
        <Grid container>
          {appRedirectionVariation &&
            appRedirectionExperimentConfig.isOverlayEnabled && (
              <Overlay variation={appRedirectionVariation as Variation} />
            )}
          <HeadlineGridItem size={{ xs: areBadgesIncluded ? 8 : 12, md: 6 }}>
            <Headline variant="h1" suppressHydrationWarning>
              {headlineWithMacros || HEADLINE_DEFAULT}
            </Headline>
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />
              <PartnerPanelsWrapper>
                <PartnerPanels partners={partners} />
              </PartnerPanelsWrapper>

              <LegalTextWrapper
                sx={{ visibility: showLegalText ? 'visible' : 'hidden' }}
              >
                <LegalTextSEAShort />
              </LegalTextWrapper>
            </Box>
          </HeadlineGridItem>
          <Grid
            size={{ xs: 4 }}
            sx={{
              display: { xs: 'flex', md: 'none' },
              textAlign: 'center',
              justifyContent: 'flex-end',
              visibility: mobileTopBadge ? 'visible' : 'hidden',
            }}
          >
            {mobileTopBadge && <Badge content={mobileTopBadge} />}
          </Grid>
          <Grid
            size={{ xs: 12 }}
            sx={{
              display: { xs: 'block', md: 'none' },
              textAlign: 'center',
            }}
          >
            <SellingPoints sellingPoints={bulletPoints} listIcon={listIcon} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            {isAppDownloadSectionEnabled && <AppDownloadSection />}

            <LoanSelector
              ctaText={ctaText}
              hideTvBanner={!isTvBannerEnabled}
              isCreditCardCategoryEnabled={showCreditcardOption}
              additionalParams={additionalParams}
              appRedirectionVariation={appRedirectionVariation}
            >
              <DesktopBadgeWrapper>
                <HideBadgeSmallerMedium>
                  {badges?.map((badge, index) => (
                    <Badge key={index} content={badge} />
                  ))}
                </HideBadgeSmallerMedium>
              </DesktopBadgeWrapper>
            </LoanSelector>
          </Grid>
          <Box sx={{ display: { xs: 'block', md: 'none' }, width: '100%' }}>
            <PartnerPanelsWrapper>
              <Box
                sx={{
                  visibility: mobileBottomBadge ? 'visible' : 'hidden',
                  display: mobileBottomBadge ? 'block' : 'none',
                }}
              >
                {mobileBottomBadge && <Badge content={mobileBottomBadge} />}
              </Box>
              <PartnerPanels partners={partners} />
            </PartnerPanelsWrapper>

            <LegalTextWrapper>
              <Box
                sx={{
                  visibility: showLegalText ? 'visible' : 'hidden',
                  display: showLegalText ? 'block' : 'none',
                }}
              >
                <LegalTextSEAShort />
              </Box>
              <LegalTextGoogle />
            </LegalTextWrapper>
          </Box>
        </Grid>
      </LpContainer>
    </Box>
  );
};
